import {
  convertSnakeCaseToReadableString,
  getUniqueId,
  type TFunction,
} from "@jugl-web/utils";
import {
  InstagramAccountModel,
  TwilioAccountModel,
  WebChatBotAccountModel,
  WhatsappAgentModel,
  CRMChannel,
  EmailAccountModel,
} from "@jugl-web/rest-api";
import type {
  ContactInfoChannelsValues,
  ContactInfoVisitorIntelligenceValues,
  ContactInfoProfileValues,
  ContactIntelligenceRowData,
} from "./types";

export const getProfileSectionRows = (
  profileValues: ContactInfoProfileValues,
  t: TFunction
): ContactIntelligenceRowData[] => [
  {
    id: "phone",
    label: t({
      id: "common.phone-number",
      defaultMessage: "Phone Number",
    }),
    value: profileValues.phoneNumber ?? null,
  },
  {
    id: "email",
    label: t({
      id: "common.email",
      defaultMessage: "Email",
    }),
    value: profileValues.email ?? null,
  },
  ...Object.entries(profileValues.info ?? {}).map(([fieldKey, value]) => ({
    id: fieldKey,
    label: convertSnakeCaseToReadableString(fieldKey),
    value: value ?? null,
  })),
];

export const getVistorIntelligenceSectionRows = (
  visitorIntelligence: ContactInfoVisitorIntelligenceValues,
  t: TFunction
): ContactIntelligenceRowData[] => [
  {
    id: "phone",
    label: t({
      id: "common.phone-number",
      defaultMessage: "Phone Number",
    }),
    value: visitorIntelligence.phoneNumber ?? null,
  },
  {
    id: "personal-email",
    label: t({
      id: "contacts-page.personal-email",
      defaultMessage: "Personal Email",
    }),
    value: visitorIntelligence.personalEmail ?? null,
  },
  {
    id: "business-email",
    label: t({
      id: "contacts-page.business-email",
      defaultMessage: "Business Email",
    }),
    value: visitorIntelligence.businessEmail ?? null,
  },
  {
    id: "location",
    label: t({
      id: "contacts-page.location",
      defaultMessage: "Location",
    }),
    value: visitorIntelligence.location ?? null,
  },
  {
    id: "personal-linkedIn",
    label: t({
      id: "contacts-page.personal-linkedin",
      defaultMessage: "Personal LinkedIn",
    }),
    value: visitorIntelligence.linkedin ?? null,
  },
  {
    id: "company",
    label: t({
      id: "contacts-page.company",
      defaultMessage: "Company",
    }),
    value: visitorIntelligence.company ?? null,
  },
  {
    id: "revenue",
    label: t({
      id: "contacts-page.revenue",
      defaultMessage: "Revenue",
    }),
    value: visitorIntelligence.revenue ?? null,
  },
  {
    id: "industry",
    label: t({
      id: "contacts-page.industry",
      defaultMessage: "Industry",
    }),
    value: visitorIntelligence.industry ?? null,
  },
  {
    id: "title",
    label: t({
      id: "contacts-page.title",
      defaultMessage: "Title",
    }),
    value: visitorIntelligence.title ?? null,
  },
  {
    id: "website",
    label: t({
      id: "contacts-page.website",
      defaultMessage: "Website",
    }),
    value: visitorIntelligence.website ?? null,
  },
  {
    id: "business-linkedIn",
    label: t({
      id: "contacts-page.business-linkedin",
      defaultMessage: "Business LinkedIn",
    }),
    value: visitorIntelligence.companyLinkedin ?? null,
  },
];

const mapContactSourceToCrmChannel = (src: string): CRMChannel | null => {
  switch (src) {
    case CRMChannel.whatsapp:
      return CRMChannel.whatsapp;
    case CRMChannel.instagram:
      return CRMChannel.instagram;
    case CRMChannel.chatbot:
      return CRMChannel.chatbot;
    case CRMChannel.twilio:
      return CRMChannel.twilio;
    case CRMChannel.email:
      return CRMChannel.email;
    default:
      return null;
  }
};

const getChannelLabel = (
  channel: CRMChannel,
  occurrence: number,
  t: TFunction
): string => {
  const labels: Record<CRMChannel, string> = {
    [CRMChannel.whatsapp]: t({
      id: "contacts-page.whatsapp",
      defaultMessage: "WhatsApp",
    }),
    [CRMChannel.instagram]: t({
      id: "contacts-page.instagram",
      defaultMessage: "Instagram",
    }),
    [CRMChannel.chatbot]: t({
      id: "contacts-page.web-chat",
      defaultMessage: "Web chat",
    }),
    [CRMChannel.twilio]: t({
      id: "contacts-page.text-sms",
      defaultMessage: "Text (SMS)",
    }),
    [CRMChannel.email]: t({
      id: "contacts-page.gmail",
      defaultMessage: "Gmail",
    }),
  };

  const baseLabel = labels[channel];

  return occurrence > 1 ? `${baseLabel} #${occurrence}` : baseLabel;
};

const isNonEmptyAccount = <T extends object | null | undefined>(
  account: T
): account is Exclude<T, null | undefined> =>
  !!account && Object.keys(account).length > 0;

export const getContactChannelConnectionStatus = (crmConfig?: {
  chatbot: WebChatBotAccountModel | null;
  instagram: InstagramAccountModel | null;
  twilio: TwilioAccountModel | null;
  whatsapp: WhatsappAgentModel | null;
  email: EmailAccountModel | null;
}): Record<CRMChannel, boolean> => {
  const whatsappAgent = isNonEmptyAccount(crmConfig?.whatsapp)
    ? crmConfig.whatsapp
    : undefined;
  const instagramAccount = isNonEmptyAccount(crmConfig?.instagram)
    ? crmConfig.instagram
    : undefined;
  const webChatBotAccount = isNonEmptyAccount(crmConfig?.chatbot)
    ? crmConfig.chatbot
    : undefined;
  const emailAccount = isNonEmptyAccount(crmConfig?.email)
    ? crmConfig.email
    : undefined;

  return {
    [CRMChannel.whatsapp]: whatsappAgent?.phone_number.status === "CONNECTED",
    [CRMChannel.instagram]: instagramAccount?.status === "connected",
    [CRMChannel.chatbot]: !!webChatBotAccount?.enabled,
    [CRMChannel.twilio]: !!crmConfig?.twilio?.id,
    [CRMChannel.email]: !!emailAccount?.id,
  };
};

export const getChannelsSectionRows = (
  channelValues: ContactInfoChannelsValues,
  t: TFunction,
  channelConnectionStatus?: Record<CRMChannel, boolean>
): ContactIntelligenceRowData[] => {
  const occurrencesByChannel = new Map<CRMChannel, number>();

  return channelValues.conversations.flatMap(({ conv_id, src }) => {
    const normalizedSource = src.trim().toLowerCase();
    const channel = mapContactSourceToCrmChannel(normalizedSource);
    if (!channel) {
      return [];
    }

    const occurrence = (occurrencesByChannel.get(channel) ?? 0) + 1;

    occurrencesByChannel.set(channel, occurrence);

    const label = getChannelLabel(channel, occurrence, t);

    return [
      {
        id: getUniqueId(),
        label,
        value: {
          type: "conversation",
          value: t({
            id: "contacts-page.conversation",
            defaultMessage: "Conversation",
          }),
          channel,
          convId: conv_id,
          isChannelAccountConnected: !!channelConnectionStatus?.[channel],
        },
      },
    ];
  });
};
